import { MASTER_ROUTES } from '../../../config/api-routes';
import instance from '../../../config/instance';
import { paramsToQueryString } from '../../../utility/helpers/commonFunction';

export const getCountryMasterListApi = async (params = {}) => {
    let queryParameter = paramsToQueryString(params);
    const response = await instance.get(MASTER_ROUTES.GET_COUNTRY_MASTER_LIST + queryParameter);
    return response.data.data || response.data;
};

/**
 * Get city master list
 * This API fetches the complete list of cities for dropdown selections
 * @returns {Promise} - Array of city objects with id, name, code, province_id
 */
export const getCityMasterListApi = async (params = {}) => {
    let queryParameter = paramsToQueryString(params);
    const response = await instance.get(MASTER_ROUTES.GET_CITY_MASTER_LIST + queryParameter);
    return response.data.data || response.data;
};

/**
 * Get province master list
 * This API fetches the complete list of provinces/states for dropdown selections
 * @returns {Promise} - Array of province objects with id, name, code, country_id
 */
export const getProvinceMasterListApi = async (params = {}) => {
    let queryParameter = paramsToQueryString(params);
    const response = await instance.get(MASTER_ROUTES.GET_PROVINCE_MASTER_LIST + queryParameter);
    return response.data.data || response.data;
};

/**
 * Get vehicle year master list
 * This API fetches the complete list of vehicle years for dropdown selections
 * @returns {Promise} - Array of vehicle year objects with id, name, code, country_id
 * @param {object} params - Query parameters
 * @param {number} params.page - Page number
 * @param {number} params.limit - Number of items per page
 * @param {number} params.year - Vehicle year filter (optional)
 * @returns {Promise} - Array of vehicle year objects
 */
export const getVehicleYearListApi = async (params = {}) => {
    let queryParameter = paramsToQueryString(params);
    const response = await instance.get(MASTER_ROUTES.GET_VEHICLE_YEAR_LIST + queryParameter);
    return response.data.data || response.data;
};

/**
 * Get vehicle make master list
 * This API fetches the complete list of vehicle makes for dropdown selections
 * @param {object} params - Query parameters
 * @param {number} params.page - Page number
 * @param {number} params.limit - Number of items per page
 * @param {number} params.year - Vehicle year filter (optional)
 * @returns {Promise} - Array of vehicle make objects
 */
export const getVehicleMakeListApi = async (params = {}) => {
    const queryString = paramsToQueryString(params);
    const url = `${MASTER_ROUTES.GET_VEHICLE_MAKE_LIST}${queryString}`;

    const response = await instance.get(url);
    return response.data.data || response.data;
};
/**
 * Get vehicle model master list
 * This API fetches the complete list of vehicle models for dropdown selections
 * @param {object} params - Query parameters
 * @param {number} params.page - Page number
 * @param {number} params.limit - Number of items per page
 * @param {number} params.year - Vehicle year filter (optional)
 * @param {number} params.make - Vehicle make filter (optional)
 * @returns {Promise} - Array of vehicle model objects
 */
export const getVehicleModelListApi = async (params = {}) => {
    const queryString = paramsToQueryString(params);
    const url = `${MASTER_ROUTES.GET_VEHICLE_MODEL_LIST}${queryString}`;

    const response = await instance.get(url);
    return response.data.data || response.data;
};

/**
 * Get vehicle trim master list
 * This API fetches the complete list of vehicle trims for dropdown selections
 * @param {object} params - Query parameters
 * @param {number} params.page - Page number
 * @param {number} params.limit - Number of items per page
 * @param {number} params.year - Vehicle year filter (optional)
 * @param {number} params.make - Vehicle make filter (optional)
 * @param {number} params.model - Vehicle model filter (optional)
 * @returns {Promise} - Array of vehicle trim objects
 */
export const getVehicleTrimListApi = async (params = {}) => {
    const queryString = paramsToQueryString(params);
    const url = `${MASTER_ROUTES.GET_VEHICLE_TRIM_LIST}${queryString}`;

    const response = await instance.get(url);
    return response.data.data || response.data;
};

/**
 * Get service category master list
 * This API fetches the complete list of service categories for dropdown selections
 * @param {object} params - Query parameters
 * @param {number} params.limit - Number of items per page
 * @returns {Promise} - Array of service category objects
 */
export const getServiceCategoryListApi = async (params = {}) => {
    const queryParams = new URLSearchParams();

    Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
            queryParams.append(key, params[key]);
        }
    });

    const queryString = queryParams.toString();
    const url = queryString ? `${MASTER_ROUTES.GET_SERVICE_CATEGORY_LIST}?${queryString}` : MASTER_ROUTES.GET_SERVICE_CATEGORY_LIST;

    const response = await instance.get(url);
    return response.data.data || response.data;
};

export const getSelectedServiceCategoryListApi = async (params = {}) => {
    const queryParams = new URLSearchParams();

    Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
            // Handle array parameters (filters)
            if (Array.isArray(params[key])) {
                params[key].forEach((item) => {
                    if (item !== undefined && item !== null && item !== '') {
                        queryParams.append(key, item);
                    }
                });
            } else {
                queryParams.append(key, params[key]);
            }
        }
    });

    const queryString = queryParams.toString();
    const url = queryString ? `${MASTER_ROUTES.GET_SELECTED_SERVICE_CATEGORY_LIST}?${queryString}` : MASTER_ROUTES.GET_SELECTED_SERVICE_CATEGORY_LIST;

    const response = await instance.get(url);
    return response.data.data || response.data;
};

/**
 * Get mechanic/assignee details list
 * This API fetches the list of mechanics/assignees for appointment assignment
 * @returns {Promise} - Array of mechanic objects with _id, name, isDefault, etc.
 */
export const getMechanicDetailsApi = async () => {
    const response = await instance.get(MASTER_ROUTES.GET_MECHANIC_DETAILS);
    return response.data.data || response.data;
};

/**
 * Create vehicle make mutation
 * @param {object} payload - Make creation payload
 * @returns {Promise} - Created make data
 */
export const createVehicleMakeApi = async (payload) => {
    const response = await instance.post(MASTER_ROUTES.CREATE_VEHICLE_MAKE, payload);
    return response.data.data || response.data;
};

/**
 * Create vehicle model mutation
 * @param {object} payload - Model creation payload
 * @returns {Promise} - Created model data
 */
export const createVehicleModelApi = async (payload) => {
    const response = await instance.post(MASTER_ROUTES.CREATE_VEHICLE_MODEL, payload);
    return response.data.data || response.data;
};

/**
 * Create vehicle trim mutation
 * @param {object} payload - Trim creation payload
 * @returns {Promise} - Created trim data
 */
export const createVehicleTrimApi = async (payload) => {
    const response = await instance.post(MASTER_ROUTES.CREATE_VEHICLE_TRIM, payload);
    return response.data.data || response.data;
};

/**
 * Decode VIN mutation
 * @param {string} vin - VIN number
 * @returns {Promise} - Decoded vehicle data
 */
export const decodeVINApi = async (vin) => {
    const response = await instance.get(`${MASTER_ROUTES.DECODE_VIN}${vin}`);
    return response.data;
};

/**
 * Decode license plate mutation
 * @param {object} params - License decode parameters
 * @returns {Promise} - Decoded vehicle data
 */
export const decodeLicenseApi = async (params) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await instance.get(`${MASTER_ROUTES.DECODE_LICENSE}?${queryString}`);
    return response.data;
};
